<script setup>
// import { useRouter } from "vue-router";
import { useHead } from "@vueuse/head";
import CtaSection from "@/components/molecules/CtaSection.vue";

// const router = useRouter();

// const navigateTo = (path) => {
//   router.push(path);
// };

// SEO構造化データ（Article スキーマ）
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "VTuber・ライバーのクラファンの始め方-クラウドファンディングの始め方をわかりやすく解説",
  description:
    "VTuberやライバーがクラウドファンディングを始めるための基本を、目的設定からリターン設計・成功のコツまでわかりやすく解説します。",
  datePublished: "2025-10-30",
  dateModified: "2025-10-30",
  author: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  publisher: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  image: "/img/airticlepic/article2.webp",
};

// メタ情報と構造化データを設定
useHead({
  title:
    "VTuber・ライバーのクラファンの始め方-クラウドファンディングの始め方をわかりやすく解説 | SukiSukiらいぶすてーしょん",
  meta: [
    {
      name: "description",
      content:
        "VTuberやライバーがクラウドファンディングを始めるための基本を、目的設定からリターン設計・成功のコツまでわかりやすく解説します。",
    },
    {
      property: "og:title",
      content:
        "VTuber・ライバーのクラファンの始め方-クラウドファンディングの始め方をわかりやすく解説 | SukiSukiらいぶすてーしょん",
    },
    {
      property: "og:description",
      content:
        "VTuberやライバーがクラウドファンディングを始めるための基本を、目的設定からリターン設計・成功のコツまでわかりやすく解説します。",
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:image",
      content: "https://sukisuki.live/img/airticlepic/article2.webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(articleSchema),
    },
  ],
});
</script>

<template>
  <v-app>
    <!-- ヒーローセクション -->
    <figure class="hero-section">
      <img
        src="/img/airticlepic/article2.webp"
        alt="VTuber・ライバーのクラファンの始め方-クラウドファンディングの始め方をわかりやすく解説するヒーロー画像"
      />
    </figure>

    <!-- パンくずナビゲーション -->
    <nav aria-label="パンくずリスト" class="breadcrumb-section">
      <v-container>
        <ol class="breadcrumb">
          <li><a href="/academy">TOP</a></li>
          <li>配信クラファン</li>
        </ol>
      </v-container>
    </nav>

    <!-- メインコンテンツ -->
    <main>
      <article class="article-content">
        <v-container>
          <!-- 記事タイトル -->
          <div class="article-title-box">
            <h1>
              VTuber・ライバーのクラファンの始め方-クラウドファンディングの始め方をわかりやすく解説
            </h1>
          </div>

          <!-- 更新日 -->
          <div class="update-date-box">
            <time datetime="2025-10-27">更新日:2025年10月30日</time>
          </div>

          <!-- イントロダクション -->
          <div class="intro-box">
            <p>
              VTuberやライバーがクラウドファンディングを始めるための基本を、目的設定からリターン設計・成功のコツまでわかりやすく解説します。
            </p>
          </div>

          <section class="learn-section">
            <h2 class="learn-title">この記事でわかる内容</h2>
            <div class="learn-content">
              <p>
                VTuberやライバーとして活動していると、「新しい3Dモデルを作りたい」「オリジナルライブイベントを開催したい」「新衣装を作りたい」など、ファンと一緒に“目標を叶える”機会が出てきます。
                そんなときに活用できるのが、クラウドファンディング（クラファン）です。
              </p>
              <p>
                この記事では、VTuber・ライバーがクラファンを始めるための基本知識とステップ、そして自然に始められる方法をわかりやすく解説します。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">クラウドファンディングとは？</h2>
            <div class="learn-content">
              <p>
                クラウドファンディングは、インターネット上で多くの人から少しずつ資金を集め、プロジェクトを実現する仕組みです。
                個人でも始められ、「購入型（リターンあり）」や「寄付型」などさまざまな形式があります。
              </p>
              <p>
                VTuber・ライバーの場合は、ファンと一緒に目標を作り上げるプロセスそのものがコンテンツになる点が大きな特徴です。
                配信を通じて進捗を共有したり、達成の瞬間をファンと一緒に祝えるのは、ライブ活動ならではの強みです。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">VTuber・ライバーがクラファンを行うメリット</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">1. ファンと一緒に夢を叶えられる</span>
              </p>
              <p>
                クラファンは「支援」ではなく「共創」です。
                たとえば「新3Dモデル制作」「オリジナル楽曲の制作」「リアルイベント開催」など、ファンが自分の応援で推しの夢が実現する体験が得られます。
              </p>
              <p>
                <span class="bold-text">2. 新しいファンとの出会いのきっかけになる</span>
              </p>
              <p>
                クラファンページはSNSや検索経由で多くの人に届くため、新規ファンの獲得や話題化にもつながります。
                初めて知った人が、クラファンをきっかけに配信を見に来るケースも少なくありません。
              </p>
              <p>
                <span class="bold-text">3. “活動の幅”を広げる資金調達手段に</span>
              </p>
              <p>
                通常のライブ配信のみでは収益化の手段が限られがちですが、クラファンを活用すれば新しい挑戦をリスクなく実現できます。
                制作費や機材費を事前に集められることで、安心して活動の幅を広げることができます。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">VTuber・ライバーのクラファンの実例（成功のポイント）</h2>
            <div class="learn-content">
              <p>
                CAMPFIREなどでは、VTuberが3D化を目指すクラファンや、オリジナル楽曲制作のクラファンなどが多数行われています。
                成功しているプロジェクトに共通するのは、次の3点です。
              </p>
              <p>
                <span class="bold-text"
                  >1. ストーリーが明確：「なぜ今、この挑戦をするのか」が伝わる。</span
                >
              </p>
              <p>
                <span class="bold-text"
                  >2. ビジュアルや動画が丁寧：サムネや説明動画の完成度が高く、信頼を感じる。</span
                ><br />
              </p>
              <p>
                <span class="bold-text">3. 配信での報告が多い：ファンと一緒に進捗を楽しむ。</span
                ><br />
                リターンとしてオリジナルグッズ制作などを実施。
              </p>
              <p>
                特に、VTuber・ライバーの場合は“活動の一部としての配信”がそのままプロモーションになる点が強みです。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">クラファンを始めるまでの流れ</h2>
            <div class="learn-content">
              <p><span class="bold-text">ステップ①：目的とストーリーを決める</span></p>
              <p>
                「何のために」「どんな未来を目指すのか」を具体的に。
                ファンが応援したくなるような理由づけを意識しましょう。
              </p>
              <p><span class="bold-text">ステップ②：リターン（支援特典）を設計する</span></p>
              <p>例としては以下のようなものがあります：</p>
              <p>・アクリルスタンド、缶バッチ</p>
              <p>・高額プランでは直筆サインやオンラインイベントなど</p>
              <p>“推し活”の文脈では、「支援＝特別な体験」になるリターンが好まれます。</p>
              <p><span class="bold-text">ステップ③：プラットフォームを選ぶ</span></p>
              <p>
                Sukisukiらいぶすてーしょんの配信クラファン、CAMPFIREなどVTuberの事例が多い国内プラットフォームを利用できます。
                手数料・審査・方式（All-in／All-or-nothing）を確認しましょう。
              </p>
            </div>
          </section>

          <!-- CTA ボタン -->
          <CtaSection article-id="academy-article-2" />

          <section class="learn-section">
            <h2 class="learn-title">「配信クラファン」という新しい選択肢</h2>
            <div class="learn-content">
              <p>
                ここで紹介したような一般的なクラファンに加えて、最近では「配信を通じて行うクラファン」の仕組みも登場しています。
              </p>
              <p>
                たとえば、「SukiSukiらいぶすてーしょんの配信クラファン」は、VTuberやライバーがライブ配信をしながら
                ファンと一緒に支援を集められる“配信イベント型クラファンプラットフォーム”です。
              </p>
              <p>
                通常のクラファンで大きな負担となる部分を、SukiSuki運営チームが丸ごと対応します。クリエイターは配信内容の構想やファンとの関係づくりに集中できます。
              </p>
              <p>・ページ制作</p>
              <p>・リターン設計</p>
              <p>・デザインや発送手配</p>
              <p>などの面倒な作業をすべて代行。</p>
              <p>配信者は「素材を用意して、配信するだけ」でプロ品質のクラファンを開催できます。</p>
              <p>
                また、クラファン中にリアルタイムで支援者の名前が表示され、その場で「ありがとう」を伝えられる仕組みも特徴です。ファンと一緒に“挑戦の瞬間”を共有できる新しいクラファン体験として注目されています。
              </p>
            </div>
          </section>

          <section class="learn-section">
            <h2 class="learn-title">まとめ｜クラファンは「応援を形にする」第一歩</h2>
            <div class="learn-content">
              <p>
                クラウドファンディングは、VTuberやライバーがファンと一緒に夢を実現するための仕組みです。重要なのは、「何をしたいか」よりも「なぜやりたいか」を丁寧に伝えること。
              </p>
              <p>
                そして、難しい準備や運営に時間を割きたくない場合は、SukiSukiらいぶすてーしょんのような配信型クラファンを活用すれば、“配信そのもの”をファンとの挑戦の場に変えることができます。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">あなたも今日から始められる</h2>
            <div class="learn-content">
              <p>クラファンを始めるのに、特別な資格や事務所所属は不要です。</p>
              <p>
                「配信を通じて夢を叶えたい」「ファンと一緒に企画を作りたい」と思ったら、
                まずは一歩踏み出してみましょう。
              </p>
            </div>
          </section>
        </v-container>
      </article>
    </main>

    <!-- CTA ボタン -->
    <CtaSection class="mb-10" article-id="academy-article-2" />
  </v-app>
</template>

<style scoped>
/* ヒーローセクション */
.hero-section {
  padding: 20px;
  width: 100%;
}

.hero-section img {
  width: 100%;
  height: auto;
  display: block;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-main-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.hero-left-content {
  flex: 1;
  text-align: left;
}

.hero-tag {
  background-color: #c8007f;
  color: white;
  padding: 8px 20px;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 20px;
}

.hero-title {
  font-size: 2.5rem;
  color: #6b4c93;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: #6b4c93;
  margin: 0;
}

.hero-right-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-image {
  max-width: 100%;
  height: auto;
}

/* パンくず */
.breadcrumb-section {
  background-color: white;
  /* padding: 15px 0; */
}

.breadcrumb {
  color: #666;
  font-size: 14px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 8px;
  text-align: left;
  display: flex;
  list-style: none;
  margin: 0;
  gap: 8px;
}

.breadcrumb li {
  display: inline-block;
}

.breadcrumb li:not(:last-child)::after {
  content: ">";
  margin-left: 8px;
}

.breadcrumb a {
  color: #666;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* メインコンテンツ */
::v-deep(.v-container) {
  text-align: left;
  padding: 0 !important;
}

.article-content {
  /* margin: 40px 0; */
  text-align: left;
}

.article-content h2,
.article-content h3 {
  text-align: left;
}

.article-title-box {
  margin-bottom: 5px;
}

.article-title-box h1,
.article-title-box h2 {
  color: #333;
  font-size: 18px;
  margin: 0;
  font-weight: bold;
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
  line-height: 1.6;
}

.update-date-box {
  padding: 10px 20px;
  margin-bottom: 10px;
}

.update-date-box p,
.update-date-box time {
  color: #333;
  margin: 0;
  font-size: 14px;
  text-align: left;
}

.intro-box {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  margin-left: 20px;
  margin-right: 20px;
  font-size: 14px;
}

.intro-box p {
  color: #333;
  margin: 0;
  line-height: 1.6;
  text-align: left;
}

.learn-section {
  margin: 30px 0;
}

.learn-title {
  color: #333;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 20px;
  text-align: left;
  background-color: #ffe0e0;
  padding: 15px 0 15px 20px;
  position: relative;
  line-height: 1.6;
}

.learn-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  background-color: #982da6;
}

.learn-content {
  margin-left: 20px;
  text-align: left;
}

.learn-content p {
  color: #333;
  line-height: 1.6;
  margin: 0 20px 15px 0;
  text-align: left;
  font-size: 16px;
}

/* CTA ボタン */
.cta-section {
  padding-bottom: 40px;
  margin-left: 40px;
  margin-right: 40px;
}

.cta-sectionnaka {
  /* padding-bottom: 40px; */
  margin-left: 20px;
  margin-right: 20px;
}

.cta-button {
  background: #da3990;
  color: white !important;
  font-size: 14px;
  font-weight: bold;
  height: 60px;
}

.custom-content {
  line-height: 1.6;
}

.bold-text {
  font-weight: bold;
}

/* 比較テーブル */
.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
}

.comparison-table th,
.comparison-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
}

.comparison-table th {
  background-color: #ffe0e0;
  font-weight: bold;
  color: #333;
}

.comparison-table td {
  background-color: #fff;
}

.comparison-table tr:hover {
  background-color: #f9f9f9;
}

.learn-content-table {
  margin-left: 20px;
  margin-right: 20px;
  text-align: left;
  line-height: 1.6;
}

@media (min-width: 750px) {
  .hero-section {
    padding: 0 25%;
  }

  .hero-section img {
    width: 100%;
    height: auto;
    display: block;
  }
  .breadcrumb {
    padding: 10px 25%;
  }
  .v-container {
    max-width: 5000px !important;
  }
  .article-title-box {
    padding: 0 25%;
  }
  .article-title-box h1,
  .article-title-box h2 {
    font-size: 20px;
    padding: 0;
    line-height: 1.6;
  }
  .update-date-box {
    padding: 10px 25%;
  }
  .update-date-box p,
  .update-date-box time {
    padding: 0;
  }
  .intro-box {
    padding: 10px;
  }
  .intro-box p {
    font-size: 16px;
    line-height: 1.6;
  }
  .intro-box {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-title {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content p {
    margin-left: 0;
    margin-right: 0;
  }
  .cta-section {
    margin-left: 30%;
    margin-right: 30%;
  }
  .cta-sectionnaka {
    margin-left: 30%;
    margin-right: 30%;
  }
  .learn-content-table {
    margin-left: 25%;
    margin-right: 25%;
    text-align: left;
  }
}
</style>
